import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage, readManagedRoutes, deleteIpRoute, readIpRoutes } from '../../../services';
import './RoutesList.css';

// Badge de status da rota (active, staged, configured-not-active, runtime-not-configured, orphan).
const StatusBadge = ({ status }) => {
  const map = {
    active: { label: 'Ativa', cls: 'badge-success' },
    staged: { label: 'Em stage', cls: 'badge-warning' },
    'configured-not-active': { label: 'Configurada (inativa)', cls: 'badge-warning' },
    'runtime-not-configured': { label: 'Runtime (não persistida)', cls: 'badge-warning' },
    orphan: { label: 'Órfã', cls: 'badge-danger' },
  };
  const m = map[status] || { label: status, cls: 'badge-default' };
  return <span className={`route-badge ${m.cls}`}>{m.label}</span>;
};

function RoutesList() {
  const history = useHistory();
  const [managedRoutes, setManagedRoutes] = useState([]);
  const [runtimeRoutes, setRuntimeRoutes] = useState([]);
  const [tab, setTab] = useState('managed'); // 'managed' | 'runtime'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [deleteState, setDeleteState] = useState({ id: null, busy: false });

  const reloadManaged = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    const resp = await readManagedRoutes();
    if (resp.ok) {
      setManagedRoutes(Array.isArray(resp.data) ? resp.data : []);
    } else {
      setErrorMsg(resp.message || 'Falha ao listar rotas gerenciadas.');
      setManagedRoutes([]);
    }
    setLoading(false);
  }, []);

  const reloadRuntime = useCallback(async () => {
    setLoading(true);
    const r = await readIpRoutes();
    if (Array.isArray(r) && Array.isArray(r[0])) setRuntimeRoutes(r[0]);
    setLoading(false);
  }, []);

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user: { role } } = dataUser;
    if (role !== 'root') return history.push('/home');
    await Promise.all([reloadManaged(), reloadRuntime()]);
  }, [history, reloadManaged, reloadRuntime]);

  const onAskDelete = (id) => setDeleteState({ id, busy: false });
  const onCancelDelete = () => setDeleteState({ id: null, busy: false });

  const onConfirmDelete = async () => {
    if (!deleteState.id) return;
    setDeleteState((s) => ({ ...s, busy: true }));
    const resp = await deleteIpRoute(deleteState.id);
    if (resp.ok) {
      await reloadManaged();
      await reloadRuntime();
    } else {
      setErrorMsg(resp.message || 'Falha ao remover rota.');
    }
    setDeleteState({ id: null, busy: false });
  };

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const renderManagedTable = () => (
    <table className="routes-table">
      <thead className="routes-table-header">
        <tr>
          <th>Destino</th>
          <th>Via</th>
          <th>Interface</th>
          <th>Métrica</th>
          <th>Criado por</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {managedRoutes.length === 0 && (
          <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>Nenhuma rota gerenciada.</td></tr>
        )}
        {managedRoutes.map((route) => (
          <tr key={route.id}>
            <td><code>{route.to}</code></td>
            <td><code>{route.via || '-'}</code></td>
            <td>{route.iface}</td>
            <td>{route.metric == null ? '-' : route.metric}</td>
            <td>{route.createdBy || '-'}</td>
            <td><StatusBadge status={route.status} /></td>
            <td>
              <button className="btn-route-delete" onClick={() => onAskDelete(route.id)}>Remover</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderRuntimeTable = () => (
    <table className="routes-table">
      <thead className="routes-table-header">
        <tr><th>Rota (kernel — `ip route`)</th></tr>
      </thead>
      <tbody>
        {runtimeRoutes.length === 0 && <tr><td>Nenhuma rota.</td></tr>}
        {runtimeRoutes.map((r, i) => <tr key={i}><td><code>{r}</code></td></tr>)}
      </tbody>
    </table>
  );

  return (
    <div className="routes-list-container">
      <div className="routes-header">
        <div className="routes-title-wrapper">
          <svg className="routes-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <div>
            <h1 className="routes-title">Rotas de Rede</h1>
            <p className="routes-subtitle">Gerenciadas pelo VigosCloud (persistentes via netplan) e estado runtime do kernel.</p>
          </div>
        </div>
      </div>

      <div className="route-tabs">
        <button className={`route-tab ${tab === 'managed' ? 'active' : ''}`} onClick={() => setTab('managed')}>Gerenciadas</button>
        <button className={`route-tab ${tab === 'runtime' ? 'active' : ''}`} onClick={() => setTab('runtime')}>Runtime</button>
        <button className="route-tab refresh" onClick={() => Promise.all([reloadManaged(), reloadRuntime()])}>Atualizar</button>
      </div>

      {errorMsg && (
        <div className="alert-message alert-danger" style={{ margin: '12px 0' }}>
          <div className="alert-body">{errorMsg}</div>
        </div>
      )}

      {loading && <div className="route-loading">Carregando...</div>}

      <div className="routes-table-wrapper">
        {tab === 'managed' ? renderManagedTable() : renderRuntimeTable()}
      </div>

      {deleteState.id != null && (
        <div className="route-modal-backdrop" onClick={onCancelDelete}>
          <div className="route-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remover rota?</h3>
            <p>Confirma a remoção da rota <strong>#{deleteState.id}</strong>? Esta ação aplica imediatamente via <code>netplan apply</code>.</p>
            <div className="route-modal-actions">
              <button onClick={onCancelDelete} disabled={deleteState.busy}>Cancelar</button>
              <button className="danger" onClick={onConfirmDelete} disabled={deleteState.busy}>
                {deleteState.busy ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoutesList;
